<template>
  <modal
    v-if="visible"
    :id="id"
    :size="size"
    :visible="visible"
    :title="capitalize(title)"
    :body="message"
    :button1-caption="computedPrimaryButtonLabel"
    :button2-caption="computedSecondaryButtonLabel"
    :button2-visible="true"
    @confirmed="() => onConfirmation(true)"
    @cancelled="() => onConfirmation(false)">
    <template #modal-after-text>
      <slot name="modal-after-text" />
      <slot name="changes" />
    </template>
  </modal>
</template>
<script setup lang="ts">
  import { capitalize, computed, Ref, ref } from 'vue';

  import Modal from '@/common/components/controls/Modal.vue';
  import i18n from '@/vueI18n';

  interface IConfirmaModalProps {
    id?: string;
    primaryButtonLabel?: string;
    secondaryButtonLabel?: string;
    size?: 'sm' | 'md' | 'lg';
  }

  const props = withDefaults(defineProps<IConfirmaModalProps>(), {
    id: 'confirm-modal',
    primaryButtonLabel: '',
    secondaryButtonLabel: '',
    size: 'sm',
  });

  const visible: Ref<boolean> = ref(false);
  const title: Ref<string> = ref();
  const message: Ref<string> = ref();

  const computedPrimaryButtonLabel = computed(
    () => props.primaryButtonLabel || capitalize(i18n.global.t('actions.ok')),
  );
  const computedSecondaryButtonLabel = computed(
    () => props.secondaryButtonLabel || capitalize(i18n.global.t('actions.cancel')),
  );

  let confirmationResolver: (confirmation: boolean) => void;

  function confirm(config: { title: string; message?: string }): Promise<boolean> {
    title.value = config.title;
    message.value = config.message;
    visible.value = true;

    return new Promise((resolve) => {
      confirmationResolver = (confirmation) => {
        visible.value = false;
        resolve(confirmation);
      };
    });
  }

  function onConfirmation(confirmation: boolean) {
    if (confirmationResolver == null) {
      // If confirmationResolver is null, it means the confirm method has not been called yet
      return;
    }

    confirmationResolver(confirmation);
  }

  defineExpose({
    confirm,
  });
</script>
